import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', password: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Adjust credits modal
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustUser, setAdjustUser] = useState(null);
  const [adjustMode, setAdjustMode] = useState('add'); // add | remove
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [adjustSuccess, setAdjustSuccess] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | inactive | admin | locked

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users/full');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (s) => {
    if (!s) return '— nunca';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };
  const formatRel = (s) => {
    if (!s) return 'nunca';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d`;
    return d.toLocaleDateString('pt-BR');
  };

  const filteredUsers = useMemo(() => {
    let list = users;
    if (filter === 'active')   list = list.filter(u => u.is_active && !u.is_locked);
    if (filter === 'inactive') list = list.filter(u => !u.is_active);
    if (filter === 'admin')    list = list.filter(u => u.is_admin);
    if (filter === 'locked')   list = list.filter(u => u.is_locked);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(u =>
        String(u.id).includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, filter, search]);

  const counts = useMemo(() => ({
    all: users.length,
    active: users.filter(u => u.is_active && !u.is_locked).length,
    inactive: users.filter(u => !u.is_active).length,
    admin: users.filter(u => u.is_admin).length,
    locked: users.filter(u => u.is_locked).length,
  }), [users]);

  // ============= Adjust credits =============
  const openAdjust = (user, mode) => {
    setAdjustUser(user);
    setAdjustMode(mode);
    setAdjustQty('');
    setAdjustReason('');
    setAdjustError('');
    setAdjustSuccess('');
    setAdjustOpen(true);
  };
  const closeAdjust = () => {
    setAdjustOpen(false);
    setAdjustUser(null);
  };
  const submitAdjust = async () => {
    setAdjustError('');
    setAdjustSuccess('');
    const qty = parseInt(adjustQty);
    if (isNaN(qty) || qty <= 0) {
      setAdjustError('Informe uma quantidade válida (> 0)');
      return;
    }
    if (qty > 100000) {
      setAdjustError('Limite máximo: 100.000');
      return;
    }
    if (adjustMode === 'remove' && qty > adjustUser.credits) {
      setAdjustError(`Saldo insuficiente. Usuário tem ${adjustUser.credits} créditos.`);
      return;
    }
    setAdjustLoading(true);
    try {
      const delta = adjustMode === 'add' ? qty : -qty;
      const r = await api.post(`/admin/users/${adjustUser.id}/credits/adjust`, {
        delta, reason: adjustReason.trim() || null,
      });
      setAdjustSuccess(`OK! Novo saldo: ${r.data.newCredits} créditos`);
      await fetchUsers();
      setTimeout(() => closeAdjust(), 1200);
    } catch (err) {
      setAdjustError(err.response?.data?.error || 'Erro ao ajustar créditos');
    } finally {
      setAdjustLoading(false);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({ username: user.username, email: user.email, password: '' });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ username: '', email: '', password: '' });
  };

  const saveEdit = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, editForm);
      await fetchUsers();
      cancelEdit();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      await fetchUsers();
      alert('Usuário excluído com sucesso');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erro ao excluir usuário');
    }
  };

  const handleResetPassword = async (userId) => {
    setResetUserId(userId);
    setShowPasswordModal(true);
    setNewPassword('');
    setResetError('');
    setResetSuccess('');
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const confirmResetPassword = async () => {
    if (!newPassword) {
      setResetError('Nova senha é obrigatória');
      return;
    }

    // Validate password
    if (newPassword.length < 8) {
      setResetError('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setResetError('A senha deve conter uma letra maiúscula');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setResetError('A senha deve conter uma letra minúscula');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setResetError('A senha deve conter um número');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setResetError('A senha deve conter um caractere especial');
      return;
    }

    setResetLoading(true);
    try {
      await api.post(`/admin/users/${resetUserId}/reset-password`, { newPassword });
      setResetSuccess('Senha resetada com sucesso!');
      setTimeout(() => {
        setShowPasswordModal(false);
        setResetSuccess('');
      }, 2000);
    } catch (err) {
      setResetError(err.response?.data?.error || 'Erro ao resetar senha');
    } finally {
      setResetLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
        <style>{`
          .au-loading { min-height: 100vh; background: #f5f5f5; display: flex; flex-direction: column; gap: 12px; align-items: center; justify-content: center; font-family: 'Lexend', sans-serif; color: #666; }
          .au-loading-spinner { width: 36px; height: 36px; border: 3px solid rgba(255,153,0,0.2); border-top-color: #f90; border-radius: 50%; animation: au-spin 0.8s linear infinite; }
          @keyframes au-spin { to { transform: rotate(360deg); } }
        `}</style>
        <div className="au-loading"><div className="au-loading-spinner"></div><p>Carregando usuários...</p></div>
      </>
    );
  }

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .au *, .au *::before, .au *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .au { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
        
        /* ===== HEADER ===== */
        .au-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .au-header-logo { width: 40px; height: 40px; flex-shrink: 0; }
        .au-header-logo img { width: 100%; height: 100%; object-fit: contain; }
        .au-header-title { font-size: 22px; font-weight: 700; color: #fff; flex: 1; }
        .au-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .au-header-back:hover { color: #fff; transform: translateX(-3px); }

        /* ===== MAIN ===== */
        .au-main { max-width: 1200px; margin: 0 auto; padding: 35px 20px; }
        .au-card {
          background: #fff; border-radius: 20px; box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          border: 1px solid rgba(0,0,0,0.04); overflow: hidden;
        }
        .au-table { width: 100%; border-collapse: collapse; }
        .au-table th {
          background: #f8f9fa; padding: 16px 20px; text-align: left;
          font-size: 13px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .au-table td { padding: 16px 20px; border-bottom: 1px solid #f0f0f0; }
        .au-table tr:hover { background: #f8f9fa; }
        .au-table tr:last-child td { border-bottom: none; }
        .au-input {
          width: 100%; padding: 10px 14px; border: 2px solid #e0e0e0; border-radius: 10px;
          font-size: 14px; outline: none; transition: all 0.3s; font-family: 'Lexend', sans-serif;
        }
        .au-input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .au-credits { color: #f90; font-weight: 700; font-size: 16px; }
        .au-credits-meta { font-size: 11px; color: #888; font-weight: 500; margin-top: 2px; }
        .au-badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
        .au-badge.admin    { background: rgba(255,153,0,0.15); color: #f90; }
        .au-badge.user     { background: #f0f0f0; color: #666; }
        .au-badge.active   { background: rgba(56,161,105,0.15); color: #2f855a; }
        .au-badge.inactive { background: #f0f0f0; color: #888; }
        .au-badge.locked   { background: rgba(229,62,62,0.15); color: #c53030; animation: au-blink 1.5s ease infinite; }
        @keyframes au-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }

        /* Filter tabs + search */
        .au-toolbar {
          padding: 16px 20px; border-bottom: 1px solid #eee;
          display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
        }
        .au-search {
          flex: 1; min-width: 220px;
          padding: 10px 14px 10px 38px; border: 1px solid #e0e0e0; border-radius: 10px;
          font-family: 'Lexend', sans-serif; font-size: 13px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: 12px center; background-size: 16px;
          outline: none; transition: all 0.2s;
        }
        .au-search:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .au-filter {
          display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
          border-radius: 10px; border: 1px solid #e0e0e0; background: #fff; cursor: pointer;
          font-family: 'Lexend', sans-serif; font-size: 13px; font-weight: 600; color: #555;
          transition: all 0.2s;
        }
        .au-filter:hover { border-color: #f90; color: #f90; }
        .au-filter.active { background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border-color: #f90; }
        .au-filter-count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 20px; height: 20px; padding: 0 6px;
          border-radius: 10px; font-size: 10px; font-weight: 700;
          background: #f0f0f0; color: #666;
        }
        .au-filter.active .au-filter-count { background: rgba(255,255,255,0.30); color: #fff; }

        /* User cell */
        .au-user-cell { display: flex; align-items: center; gap: 12px; }
        .au-avatar {
          width: 36px; height: 36px; border-radius: 50%;
          background: linear-gradient(135deg, #f90, #ff6600);
          color: #fff; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 14px; flex-shrink: 0;
        }
        .au-avatar.admin { background: linear-gradient(135deg, #1a1a2e, #16213e); border: 2px solid #f90; }
        .au-user-info-cell { min-width: 0; }
        .au-user-name { font-weight: 600; color: #222; font-size: 14px; }
        .au-user-email { font-size: 12px; color: #888; }

        /* Last login */
        .au-last-login { font-size: 12px; color: #444; }
        .au-last-login-rel { font-size: 11px; color: #888; }

        /* Code stat */
        .au-codes-cell { font-weight: 700; color: #555; }
        .au-codes-cell-mini { font-size: 11px; color: #888; font-weight: 500; }

        /* Adjust modal */
        .au-adjust-tabs { display: flex; gap: 6px; margin-bottom: 18px; padding: 4px; background: #f5f5f5; border-radius: 12px; }
        .au-adjust-tab {
          flex: 1; padding: 10px; border-radius: 8px; border: none; background: transparent;
          font-family: 'Lexend', sans-serif; font-size: 13px; font-weight: 600; color: #666; cursor: pointer;
          transition: all 0.2s;
        }
        .au-adjust-tab.active.add { background: #38a169; color: #fff; box-shadow: 0 3px 10px rgba(56,161,105,0.30); }
        .au-adjust-tab.active.remove { background: #e53e3e; color: #fff; box-shadow: 0 3px 10px rgba(229,62,62,0.30); }
        .au-adjust-info {
          background: rgba(255,153,0,0.06); border: 1px solid rgba(255,153,0,0.18);
          padding: 12px 14px; border-radius: 10px; margin-bottom: 16px;
          font-size: 13px; color: #444;
        }
        .au-adjust-info strong { color: #f90; font-size: 18px; }

        .au-btn.adjust-add { background: rgba(56,161,105,0.10); color: #2f855a; }
        .au-btn.adjust-add:hover { background: rgba(56,161,105,0.20); }
        .au-btn.adjust-remove { background: rgba(229,62,62,0.08); color: #c53030; }
        .au-btn.adjust-remove:hover { background: rgba(229,62,62,0.18); }
        .au-btn.detail { background: rgba(59,130,246,0.10); color: #2563eb; }
        .au-btn.detail:hover { background: rgba(59,130,246,0.20); }
        .au-actions { display: flex; gap: 10px; }
        .au-btn {
          display: flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px;
          font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.3s; border: none;
        }
        .au-btn.edit { background: rgba(255,153,0,0.1); color: #f90; }
        .au-btn.edit:hover { background: rgba(255,153,0,0.2); }
        .au-btn.save { background: #38a169; color: #fff; }
        .au-btn.save:hover { background: #2f855a; }
        .au-btn.cancel { background: #e53e3e; color: #fff; }
        .au-btn.cancel:hover { background: #c53030; }
        .au-btn.delete { background: rgba(229, 62, 62, 0.1); color: #e53e3e; }
        .au-btn.delete:hover { background: rgba(229, 62, 62, 0.2); }
        .au-btn.reset { background: rgba(102,51,153,0.1); color: #663399; }
        .au-btn.reset:hover { background: rgba(102,51,153,0.2); }

        /* ===== MODAL ===== */
        .au-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px; }
        .au-modal {
          background: #fff; border-radius: 20px; padding: 35px; width: 100%; max-width: 400px; position: relative;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .au-modal-close { position: absolute; top: 16px; right: 16px; background: none; border: none; font-size: 20px; color: #999; cursor: pointer; }
        .au-modal-close:hover { color: #666; }
        .au-modal-header { display: flex; align-items: center; gap: 12px; margin-bottom: 25px; }
        .au-modal-icon { width: 50px; height: 50px; background: rgba(255,153,0,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        .au-modal-icon i { font-size: 22px; color: #f90; }
        .au-modal-title { font-size: 22px; font-weight: 700; color: #222; }
        .au-alert { padding: 14px 18px; border-radius: 12px; margin-bottom: 20px; }
        .au-alert.success { background: rgba(56,161,105,0.1); border-left: 4px solid #38a169; color: #38a169; }
        .au-alert.error { background: rgba(229,62,62,0.1); border-left: 4px solid #e53e3e; color: #e53e3e; }
        .au-input-group { margin-bottom: 20px; }
        .au-input-group label { display: block; font-size: 13px; font-weight: 600; color: #666; margin-bottom: 8px; }
        .au-input-group input {
          width: 100%; padding: 12px 16px; border: 2px solid #e0e0e0; border-radius: 12px;
          font-size: 15px; outline: none; transition: all 0.3s; font-family: 'Lexend', sans-serif;
        }
        .au-input-group input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .au-input-row { display: flex; gap: 10px; }
        .au-gen-btn {
          background: #f90; color: #fff; border: none; border-radius: 12px; padding: 12px 16px;
          cursor: pointer; font-size: 14px; transition: all 0.3s;
        }
        .au-gen-btn:hover { background: #e68a00; }
        .au-submit-btn {
          width: 100%; background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
          padding: 14px; border-radius: 12px; border: none; cursor: pointer; font-weight: 600;
          font-size: 15px; transition: all 0.3s; box-shadow: 0 4px 15px rgba(255,153,0,0.3);
        }
        .au-submit-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(255,153,0,0.4); }
        .au-submit-btn:disabled { background: #999; cursor: not-allowed; box-shadow: none; }

        @media (max-width: 768px) {
          .au-header { padding: 15px 20px; }
          .au-header-title { font-size: 18px; }
          .au-main { padding: 20px 15px; }
          .au-card { padding: 20px; }
          .au-table th, .au-table td { padding: 12px 10px; font-size: 12px; }
          .au-actions { flex-direction: column; }
          .au-btn { padding: 10px 12px; justify-content: center; font-size: 12px; }
          .au-modal { padding: 25px; }
          .au-table { font-size: 12px; }
          .au-badge { font-size: 10px; padding: 4px 8px; }
          .au-credits { font-size: 14px; }
        }

        @media (max-width: 480px) {
          .au-header { padding: 12px 15px; }
          .au-header-title { font-size: 16px; }
          .au-header-back { font-size: 14px; }
          .au-main { padding: 15px 10px; }
          .au-card { padding: 15px; }
          .au-table th, .au-table td { padding: 10px 8px; font-size: 11px; }
          .au-actions { flex-direction: column; gap: 5px; }
          .au-btn { padding: 8px 10px; justify-content: center; font-size: 11px; }
          .au-modal { padding: 20px; }
          .au-table { font-size: 11px; }
          .au-badge { font-size: 9px; padding: 3px 6px; }
          .au-credits { font-size: 12px; }
          .au-input { font-size: 13px; padding: 8px 12px; }
        }
      `}</style>

      <div className="au">
        {/* Header */}
        <header className="au-header">
          <h1 className="au-header-title">Gerenciar Usuários</h1>
          <div className="au-header-back" onClick={fetchUsers} title="Atualizar">
            <i className="fas fa-sync-alt"></i> Atualizar
          </div>
          {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
        </header>

        {/* Main */}
        <main className="au-main">

        <div className="au-card">
          {/* Toolbar */}
          <div className="au-toolbar">
            <input
              type="text" className="au-search"
              placeholder="Buscar por nome, email ou ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {[
              { k: 'all',      l: 'Todos' },
              { k: 'active',   l: 'Ativos' },
              { k: 'admin',    l: 'Admins' },
              { k: 'locked',   l: 'Bloqueados' },
              { k: 'inactive', l: 'Inativos' },
            ].map(f => (
              <button
                key={f.k}
                className={`au-filter ${filter === f.k ? 'active' : ''}`}
                onClick={() => setFilter(f.k)}
              >
                {f.l} <span className="au-filter-count">{counts[f.k]}</span>
              </button>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="au-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Créditos</th>
                  <th>Códigos</th>
                  <th>Status</th>
                  <th>Último login</th>
                  <th>Cadastro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {editingUser === user.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <input type="text" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} className="au-input" placeholder="Usuário" />
                          <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="au-input" placeholder="Email" />
                        </div>
                      ) : (
                        <div className="au-user-cell">
                          <div className={`au-avatar ${user.is_admin ? 'admin' : ''}`}>
                            {(user.username || '?').slice(0,1).toUpperCase()}
                          </div>
                          <div className="au-user-info-cell">
                            <div className="au-user-name">
                              {user.username}
                              {user.is_admin && <i className="fas fa-shield-alt" style={{ marginLeft: 6, color: '#f90', fontSize: 11 }} title="Admin"></i>}
                              <span style={{ color: '#bbb', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>#{user.id}</span>
                            </div>
                            <div className="au-user-email">{user.email}</div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="au-credits">{user.credits}</span>
                      <div className="au-credits-meta">
                        {user.credits_purchased > 0 && <span title="Comprados">💳 {user.credits_purchased}</span>}
                        {user.credits_added_manual > 0 && <span title="Adicionados pelo admin" style={{ marginLeft: 8 }}>➕ {user.credits_added_manual}</span>}
                        {user.credits_removed_manual > 0 && <span title="Removidos pelo admin" style={{ marginLeft: 8, color: '#c53030' }}>➖ {user.credits_removed_manual}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="au-codes-cell">{user.codes_generated || 0}</div>
                      {user.pending_orders > 0 && (
                        <div className="au-codes-cell-mini" style={{ color: '#d97706' }}>
                          <i className="fas fa-clock"></i> {user.pending_orders} pendente{user.pending_orders !== 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    <td>
                      {user.is_locked && <span className="au-badge locked"><i className="fas fa-lock"></i> Bloqueado</span>}
                      {!user.is_locked && user.is_active && <span className="au-badge active"><i className="fas fa-check-circle"></i> Ativo</span>}
                      {!user.is_active && <span className="au-badge inactive"><i className="fas fa-circle"></i> Inativo</span>}
                    </td>
                    <td>
                      <div className="au-last-login">{formatDateTime(user.last_login)}</div>
                      <div className="au-last-login-rel">{user.last_login ? `há ${formatRel(user.last_login)}` : ''}</div>
                    </td>
                    <td>
                      <div className="au-last-login">{user.created_at ? new Date(user.created_at.replace(' ', 'T')).toLocaleDateString('pt-BR') : '—'}</div>
                    </td>
                    <td>
                      {editingUser === user.id ? (
                        <div className="au-actions">
                          <button onClick={() => saveEdit(user.id)} className="au-btn save"><i className="fas fa-save"></i> Salvar</button>
                          <button onClick={cancelEdit} className="au-btn cancel"><i className="fas fa-times"></i> Cancelar</button>
                        </div>
                      ) : (
                        <div className="au-actions">
                          <button onClick={() => navigate(`/admin/users/${user.id}`)} className="au-btn detail" title="Ver detalhes completos"><i className="fas fa-eye"></i></button>
                          <button onClick={() => startEdit(user)} className="au-btn edit" title="Editar"><i className="fas fa-edit"></i></button>
                          <button onClick={() => openAdjust(user, 'add')} className="au-btn adjust-add" title="Adicionar créditos"><i className="fas fa-plus"></i></button>
                          <button onClick={() => openAdjust(user, 'remove')} className="au-btn adjust-remove" title="Remover créditos"><i className="fas fa-minus"></i></button>
                          <button onClick={() => handleResetPassword(user.id)} className="au-btn reset" title="Resetar senha"><i className="fas fa-key"></i></button>
                          <button onClick={() => handleDeleteUser(user.id)} className="au-btn delete" title="Excluir"><i className="fas fa-trash"></i></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999' }}>
                <i className="fas fa-user-slash" style={{ fontSize: 40, color: '#ddd', marginBottom: 12, display: 'block' }}></i>
                <p>{search ? 'Nenhum usuário bate com a busca' : 'Nenhum usuário nesta categoria'}</p>
              </div>
            )}
          </div>
        </div>
        </main>

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="au-modal-overlay">
          <div className="au-modal">
            <button className="au-modal-close" onClick={() => { setShowPasswordModal(false); setResetError(''); setResetSuccess(''); setNewPassword(''); }}>✕</button>
            <div className="au-modal-header">
              <div className="au-modal-icon"><i className="fas fa-lock"></i></div>
              <h3 className="au-modal-title">Resetar Senha</h3>
            </div>
            {resetSuccess && <div className="au-alert success">{resetSuccess}</div>}
            {resetError && <div className="au-alert error">{resetError}</div>}
            <div className="au-input-group">
              <label>Nova Senha</label>
              <div className="au-input-row">
                <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Digite a nova senha" required />
                <button type="button" onClick={generateRandomPassword} className="au-gen-btn" title="Gerar senha aleatória"><i className="fas fa-sync-alt"></i></button>
              </div>
            </div>
            <button onClick={confirmResetPassword} disabled={resetLoading} className="au-submit-btn">
              {resetLoading ? 'Resetando...' : 'Resetar Senha'}
            </button>
          </div>
        </div>
      )}

      {/* Adjust Credits Modal */}
      {adjustOpen && adjustUser && (
        <div className="au-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeAdjust(); }}>
          <div className="au-modal">
            <button className="au-modal-close" onClick={closeAdjust}>✕</button>
            <div className="au-modal-header">
              <div className="au-modal-icon" style={{ background: adjustMode === 'add' ? 'rgba(56,161,105,0.15)' : 'rgba(229,62,62,0.15)' }}>
                <i className={`fas ${adjustMode === 'add' ? 'fa-plus' : 'fa-minus'}`} style={{ color: adjustMode === 'add' ? '#38a169' : '#e53e3e' }}></i>
              </div>
              <h3 className="au-modal-title">Ajustar créditos</h3>
            </div>

            <div className="au-adjust-tabs">
              <button className={`au-adjust-tab ${adjustMode === 'add' ? 'active add' : ''}`} onClick={() => setAdjustMode('add')}>
                <i className="fas fa-plus-circle"></i> Adicionar
              </button>
              <button className={`au-adjust-tab ${adjustMode === 'remove' ? 'active remove' : ''}`} onClick={() => setAdjustMode('remove')}>
                <i className="fas fa-minus-circle"></i> Remover
              </button>
            </div>

            <div className="au-adjust-info">
              Usuário: <strong style={{ fontSize: 14 }}>{adjustUser.username}</strong> ({adjustUser.email})<br/>
              Saldo atual: <strong>{adjustUser.credits}</strong> créditos
            </div>

            {adjustSuccess && <div className="au-alert success">{adjustSuccess}</div>}
            {adjustError && <div className="au-alert error">{adjustError}</div>}

            <div className="au-input-group">
              <label>Quantidade {adjustMode === 'add' ? 'a adicionar' : 'a remover'}</label>
              <input
                type="number" min="1" max={adjustMode === 'remove' ? adjustUser.credits : 100000}
                value={adjustQty}
                onChange={(e) => setAdjustQty(e.target.value)}
                placeholder="Ex: 100"
                autoFocus
              />
            </div>
            <div className="au-input-group">
              <label>Motivo (opcional)</label>
              <input
                type="text" maxLength={200}
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="Ex: Cortesia, ajuste de erro, reembolso..."
              />
            </div>

            <button
              onClick={submitAdjust}
              disabled={adjustLoading}
              className="au-submit-btn"
              style={{
                background: adjustMode === 'add'
                  ? 'linear-gradient(135deg, #38a169, #2f855a)'
                  : 'linear-gradient(135deg, #e53e3e, #c53030)',
                boxShadow: adjustMode === 'add'
                  ? '0 4px 15px rgba(56,161,105,0.30)'
                  : '0 4px 15px rgba(229,62,62,0.30)',
              }}
            >
              {adjustLoading ? 'Processando...' : (adjustMode === 'add' ? `Adicionar ${adjustQty || ''} créditos` : `Remover ${adjustQty || ''} créditos`)}
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AdminUsers;
